import { useState } from 'react'
import { db, type DiningTable, type Zone } from '../lib/db'
import { tableApi } from '../lib/api'
import { useApp } from '../lib/store'
import { whenText } from '../lib/money'
import { Sheet } from './Sheet'
import { TABLE_COLOR_LABELS, TABLE_COLORS } from './TableCard'

/** Ít lựa chọn thôi. Cho 40 biểu tượng là bắt chủ quán phải ngồi cân nhắc. */
const ICONS = ['🪑', '🍽️', '🛋️', '🍺', '🌳', '🏠', '🛵', '⭐']

interface Props {
  table: DiningTable
  zones: Zone[]
  onReserve: () => void
  onClose: () => void
}

export function TableEditor({ table, zones, onReserve, onClose }: Props) {
  const say = useApp((s) => s.say)
  const [name, setName] = useState(table.name)
  const [icon, setIcon] = useState(table.icon ?? '🪑')
  const [color, setColor] = useState(table.color ?? 'slate')
  const [seats, setSeats] = useState(table.seats)
  const [zoneId, setZoneId] = useState(table.zoneId)
  const [saving, setSaving] = useState(false)

  async function save() {
    if (!name.trim()) {
      say('Chưa đặt tên bàn')
      return
    }
    setSaving(true)

    const patch = { name: name.trim(), icon, color, seats, zoneId }
    await db.diningTables.update(table.id, patch)

    try {
      await tableApi.save({
        id: table.id,
        zoneId,
        name: patch.name,
        icon,
        color,
        gridX: table.gridX,
        gridY: table.gridY,
        gridW: table.gridW,
        gridH: table.gridH,
        seats,
        isTakeaway: table.isTakeaway,
      })
    } catch {
      say('Chưa có mạng — đã lưu trên máy này')
    }

    setSaving(false)
    onClose()
  }

  /**
   * Hỏi máy chủ trước rồi mới xoá trên máy này.
   *
   * Ngược lại thì bàn đang có khách vẫn biến mất khỏi sơ đồ trong khi máy chủ từ chối xoá —
   * người bán không thấy bàn để bấm vào, mà đơn thì vẫn treo tiền ở đó.
   */
  async function remove() {
    setSaving(true)
    try {
      await tableApi.remove(table.id)
    } catch (e) {
      setSaving(false)
      say(e instanceof Error ? e.message : 'Chưa xoá được')
      return
    }

    await db.diningTables.delete(table.id)
    setSaving(false)
    say(`Đã xoá ${table.name}`)
    onClose()
  }

  return (
    <Sheet title={table.name} onClose={onClose}>
      <label className="field">
        <span>Tên bàn</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus />
      </label>

      <div className="field">
        <span>Biểu tượng</span>
        <div className="pick-row">
          {ICONS.map((i) => (
            <button
              key={i}
              type="button"
              className="pick-icon"
              aria-pressed={i === icon}
              onClick={() => setIcon(i)}
            >
              {i}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <span>Màu bàn</span>
        <div className="pick-row">
          {Object.keys(TABLE_COLORS).map((c) => (
            <button
              key={c}
              type="button"
              className="pick-color"
              aria-pressed={c === color}
              onClick={() => setColor(c)}
              title={TABLE_COLOR_LABELS[c]}
              aria-label={TABLE_COLOR_LABELS[c]}
              style={{
                background: TABLE_COLORS[c]!.bg,
                borderColor: TABLE_COLORS[c]!.line,
              }}
            >
              {c === color ? '✓' : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="field-row">
        <label className="field">
          <span>Số chỗ</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={50}
            value={seats}
            onChange={(e) => setSeats(Number(e.target.value))}
          />
        </label>

        <label className="field">
          <span>Khu vực</span>
          <select value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <button type="button" className="row-btn" onClick={onReserve}>
        <span className="row-icon" aria-hidden="true">
          ⏰
        </span>
        <span className="row-text">
          <b>Đặt bàn trước</b>
          <small>
            {table.reservedAt
              ? `${table.reservedName ?? 'Khách đặt'} · ${whenText(table.reservedAt)}`
              : 'Chưa có ai đặt'}
          </small>
        </span>
        <span aria-hidden="true">›</span>
      </button>

      <div className="sheet-actions">
        <button type="button" className="btn btn-danger-ghost" onClick={remove} disabled={saving}>
          Xoá bàn
        </button>
        <button type="button" className="btn btn-brand grow" onClick={save} disabled={saving}>
          {saving ? 'Đang lưu…' : 'Lưu'}
        </button>
      </div>
    </Sheet>
  )
}
